import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  getTranslationBackend,
  getTranslationVersion,
  subscribeToTranslationChanges,
  type TranslateParams,
} from "./TranslationBackendRegistry";

export type TranslateFn = (
  key: string,
  defaultText: string,
  params?: TranslateParams,
) => string;

export type UseTranslationResult = {
  t: TranslateFn;
  locale: string;
  setLocale: (locale: string) => void | Promise<void>;
  availableLocales: readonly string[];
};

function useTranslationVersion(): number {
  return useSyncExternalStore(
    subscribeToTranslationChanges,
    getTranslationVersion,
    getTranslationVersion,
  );
}

/**
 * Active locale, re-rendering the component when it changes.
 */
export function useLocale(): string {
  useTranslationVersion();
  return getTranslationBackend().getLocale();
}

/**
 * Translation access for components.
 *
 * `namespace` is prefixed onto every key, so
 * `useTranslation("framework").t("common.save", "Speichern")` resolves
 * `framework.common.save` and falls back to `"Speichern"`.
 */
export function useTranslation(namespace?: string): UseTranslationResult {
  const version = useTranslationVersion();

  const t = useCallback<TranslateFn>(
    (key, defaultText, params) => {
      // `version` participates so the callback identity changes on locale
      // switches, invalidating memoised consumers.
      void version;
      const fullKey = namespace ? `${namespace}.${key}` : key;
      return getTranslationBackend().translate(fullKey, defaultText, params);
    },
    [namespace, version],
  );

  const backend = getTranslationBackend();

  return useMemo(
    () => ({
      t,
      locale: backend.getLocale(),
      setLocale: (locale: string) => backend.setLocale(locale),
      availableLocales: backend.getAvailableLocales(),
    }),
    [t, backend],
  );
}
